import React from 'react';
import { WORKFLOW_AUTO_PAUSE_CONSECUTIVE_FAILURES, type WorkflowAutoPauseResponse } from '@troia-v3/types';
import { StatusPill, getStatusVariant } from '@/shared/components/ui';
import type { WorkflowStatus } from '../types';

/**
 * Badge de status do workflow (Rascunho / Ativo / Pausado / Arquivado).
 * Wrapper fino sobre o StatusPill shared (F62) — mantém os labels pt-BR
 * do domínio; cor vem do mapa semântico central (STATUS_SEMANTICS).
 *
 * Quando `status === 'inactive'` e o workflow tem `autoPause` (pausa
 * automática — canal excluído ou falhas consecutivas, 2026-08-22), o selo
 * vira "Pausado automaticamente" (variant warning) com o motivo no `title`.
 */
const STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  inactive: 'Pausado',
  archived: 'Arquivado',
};

/** Texto do motivo da pausa automática (tooltip do selo). */
export function autoPauseReasonText(autoPause: WorkflowAutoPauseResponse): string {
  if (autoPause.reason === 'channel_deleted') {
    return `Pausado automaticamente: o canal "${autoPause.details.channelName ?? autoPause.details.channelId ?? '?'}" usado pelo workflow foi excluído.`;
  }
  return `Pausado automaticamente após ${autoPause.details.consecutiveFailures ?? WORKFLOW_AUTO_PAUSE_CONSECUTIVE_FAILURES} falhas seguidas${autoPause.details.lastError ? ` — último erro: ${autoPause.details.lastError}` : ''}.`;
}

export const WorkflowStatusBadge: React.FC<{ status: WorkflowStatus; autoPause?: WorkflowAutoPauseResponse; 'data-testid'?: string }> = ({
  status,
  autoPause,
  'data-testid': dataTestId,
}) => {
  const auto = status === 'inactive' && autoPause !== undefined;
  return (
    <span title={auto ? autoPauseReasonText(autoPause) : undefined} data-auto-pause={auto ? autoPause.reason : undefined}>
      <StatusPill
        variant={auto ? 'warning' : getStatusVariant(status)}
        label={auto ? 'Pausado automaticamente' : (STATUS_LABELS[status] ?? status)}
        data-testid={dataTestId}
      />
    </span>
  );
};

WorkflowStatusBadge.displayName = 'WorkflowStatusBadge';
